import dayjs from 'dayjs';

import { selectBuCode, selectOrderType } from '@repo/api-adapters/selectors/customer-assets.selectors';
import {
    selectBillingContact,
    selectDeliveryContact,
    selectEmployeeNo,
} from '@repo/api-adapters/selectors/customer.selectors';
import type { SelectedServices } from '@repo/api-adapters/selectors/services.selectors';
import { type OrderAsset } from '@repo/types/order';
import { type ServicePricePayload } from '@repo/types/service-price';
import { type TimeWindow } from '@repo/types/time-windows';
import { utcToLocalDateTime } from '@repo/utils';
import { invariant } from '@repo/utils/type.utils';

import {
    type DeliveryServiceWithTimeWindow,
    buildCompositeServiceId,
    findAllDeliveryServicesInArrangement,
} from './services.api.utils';

interface BuildServicePricePayloadParams {
    orderData: OrderAsset;
    services: SelectedServices;
    serviceId: string;
    selectedTimeWindow: TimeWindow;
}

interface ItemLine {
    itemType: string;
    itemNo: string;
    id: number;
    requiredQty: number;
    unitOfMeasure: string;
}

interface ResolvedDeliveryService {
    deliveryMethodId: string;
    deliveryServiceId: string;
    serviceItemLines: ItemLine[];
    timeSlot: { id: string; date: string; timeFrom: string; timeTo: string };
    serviceSubType?: string;
}

const buildTimeSlot = (
    fromDateTime: string,
    toDateTime: string,
    timeWindowId: string
): { id: string; date: string; timeFrom: string; timeTo: string } | undefined => {
    const [fromDate, fromTime] = fromDateTime.split('T');
    const [toDate, toTime] = toDateTime.split('T');

    if (!fromDate || !fromTime || !toDate || !toTime || fromDate !== toDate) {
        return;
    }

    return { id: timeWindowId, date: fromDate, timeFrom: fromTime, timeTo: toTime };
};

const getDeliveryMethodId = (matchedDeliveryService: {
    serviceId?: string;
    serviceItemType?: string;
    serviceItemNo?: string;
}) =>
    matchedDeliveryService.serviceId ??
    buildCompositeServiceId(matchedDeliveryService.serviceItemType, matchedDeliveryService.serviceItemNo);

const getValidReferenceItemLines = (
    referenceItems: ReadonlyArray<{
        itemType?: string | undefined;
        itemId?: string | undefined;
        seqNo?: number | undefined;
        quantity?: number | undefined;
        quantityType?: string | undefined;
    }>
): ItemLine[] =>
    referenceItems
        .filter(
            (
                item
            ): item is typeof item & {
                itemType: string;
                itemId: string;
                seqNo: number;
                quantity: number;
                quantityType: string;
            } =>
                Boolean(
                    item.itemType &&
                    item.itemId &&
                    typeof item.seqNo === 'number' &&
                    typeof item.quantity === 'number' &&
                    item.quantity > 0 &&
                    item.quantityType
                )
        )
        .map((item) => ({
            itemType: item.itemType,
            itemNo: item.itemId,
            id: item.seqNo,
            requiredQty: item.quantity,
            unitOfMeasure: item.quantityType,
        }));

const getTimeSlotSource = (
    deliveryService: DeliveryServiceWithTimeWindow,
    serviceId: string,
    selectedTimeWindow: TimeWindow
) => {
    if (deliveryService.serviceId === serviceId) {
        return {
            fromDateTime: utcToLocalDateTime(selectedTimeWindow.fromDateTimeUtc, selectedTimeWindow.timeZone),
            toDateTime: utcToLocalDateTime(selectedTimeWindow.toDateTimeUtc, selectedTimeWindow.timeZone),
            timeWindowId: selectedTimeWindow.timeWindowId,
        };
    }

    if (deliveryService.existingTimeWindow) {
        return {
            fromDateTime: deliveryService.existingTimeWindow.fromLocalDateTime,
            toDateTime: deliveryService.existingTimeWindow.toLocalDateTime,
            timeWindowId: deliveryService.existingTimeWindow.timeWindowId,
        };
    }

    return;
};

interface ResolveArrangementServicesParams {
    arrangementServices: DeliveryServiceWithTimeWindow[];
    services: SelectedServices;
    serviceId: string;
    selectedTimeWindow: TimeWindow;
}

const resolveArrangementServices = ({
    arrangementServices,
    services,
    serviceId,
    selectedTimeWindow,
}: ResolveArrangementServicesParams): ResolvedDeliveryService[] | undefined => {
    const resolved: ResolvedDeliveryService[] = [];
    let hasMatchedService = false;

    for (const deliveryService of arrangementServices) {
        const deliveryMethodId = getDeliveryMethodId(deliveryService);
        if (!deliveryMethodId) {
            continue;
        }

        const correspondingService = services.find((s) => s.serviceId === deliveryService.serviceId);
        if (!correspondingService?.serviceId || !correspondingService.referenceItems?.length) {
            continue;
        }

        const serviceItemLines = getValidReferenceItemLines(correspondingService.referenceItems);
        if (serviceItemLines.length === 0) {
            continue;
        }

        const isMatchedService = deliveryService.serviceId === serviceId;
        const timeSlotSource = getTimeSlotSource(deliveryService, serviceId, selectedTimeWindow);
        if (!timeSlotSource) {
            continue;
        }

        const timeSlot = buildTimeSlot(
            timeSlotSource.fromDateTime,
            timeSlotSource.toDateTime,
            timeSlotSource.timeWindowId
        );
        if (!timeSlot) {
            if (isMatchedService) {
                return;
            }
            continue;
        }

        if (isMatchedService) {
            hasMatchedService = true;
        }

        resolved.push({
            deliveryMethodId,
            deliveryServiceId: deliveryService.deliveryNo ?? correspondingService.serviceId,
            serviceItemLines,
            timeSlot,
            ...(deliveryService.serviceSubType && { serviceSubType: deliveryService.serviceSubType }),
        });
    }

    if (!hasMatchedService || resolved.length === 0) {
        return;
    }

    return resolved;
};

export const buildServicePricePayload = ({
    orderData,
    services,
    serviceId,
    selectedTimeWindow,
}: BuildServicePricePayloadParams): ServicePricePayload | undefined => {
    const buCode = selectBuCode(orderData);
    invariant(buCode, 'buildServicePricePayload: buCode is missing from order data');

    const arrangementServices = findAllDeliveryServicesInArrangement(orderData, serviceId);
    invariant(
        arrangementServices.length > 0,
        `buildServicePricePayload: no delivery services found in arrangement for serviceId "${serviceId}"`
    );

    const deliveryContact = selectDeliveryContact(orderData);
    invariant(
        deliveryContact?.zipCode && deliveryContact.countryCode,
        'buildServicePricePayload: delivery contact is missing zipCode or countryCode'
    );

    const orderType = selectOrderType(orderData);
    invariant(orderType, 'buildServicePricePayload: orderType is missing from order data');

    const serviceArea = {
        zipCode: deliveryContact.zipCode,
        countryCode: deliveryContact.countryCode,
        ...(deliveryContact.stateCode && { stateCode: deliveryContact.stateCode }),
    };

    const deliveryArrangementServices = resolveArrangementServices({
        arrangementServices,
        services,
        serviceId,
        selectedTimeWindow,
    });
    if (!deliveryArrangementServices) {
        return;
    }

    const itemLines = [
        ...new Map(
            deliveryArrangementServices
                .flatMap((deliveryArrangement) => deliveryArrangement.serviceItemLines)
                .map((line) => [line.id, line])
        ).values(),
    ];

    const deliveryServices = deliveryArrangementServices.map((service) => ({
        deliveryMethod: service.deliveryMethodId,
        deliveryMethodId: service.deliveryMethodId,
        deliveryType: orderType,
        id: service.deliveryServiceId,
        itemLineReferences: service.serviceItemLines.map((line) => ({
            lineId: line.id,
            quantity: line.requiredQty,
        })),
        serviceArea,
        timeSlots: [service.timeSlot],
        storageFromDate: dayjs().tz(selectedTimeWindow.timeZone).format('YYYY-MM-DD'),
        ...(service.serviceSubType && { solutionReference: service.serviceSubType }),
    }));

    const billingContact = selectBillingContact(orderData);
    const matchedService = arrangementServices.find((ds) => ds.serviceId === serviceId);
    const isFamilyCustomer = Boolean(deliveryContact.ikeaFamilyNumber);
    const isEmployee = Boolean(selectEmployeeNo(orderData));

    return {
        buCode,
        serviceRequest: {
            serviceArea,
            itemLines,
            deliveryServices,
            customer: {
                deliveryAddress: deliveryContact,
                billingAddress: billingContact,
                isFamilyCustomer,
                isEmployee,
            },
            pickupPoints: [],
            solutions: matchedService?.serviceSubType ? [matchedService.serviceSubType] : [],
        },
    };
};
